// =============================================================================
// Intelligence Page — Merged Control Tower + Jeopardy/Risk
//
// Single "What needs my attention?" page with:
// 1. Programme-level cards (Control Tower) — collapsible header
// 2. Site-level risk table (Jeopardy) — main content
// 3. NBA recommendations — integrated panel
//
// This replaces both the separate Control Tower and Jeopardy pages.
// =============================================================================
import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle, AlertCircle, Info, CheckCircle2, ArrowRight,
  ThumbsUp, ThumbsDown, Sparkles, ChevronDown, ChevronUp,
  Radar, Clock, Shield,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useAsync } from '../hooks/useAsync'
import { Card, Button, RagDot, Skeleton, ErrorState, EmptyState, SectionTitle } from '../components/ui'
import { fmtDate } from '../lib/format'
import { useIntelligenceSlice, setNba as storeSetNba, setCardsExpanded as storeSetCardsExpanded } from '../store/usePageSlices'
import type { ControlTowerResponse, ControlTowerCard, CardSeverity, Insights, AgentNextBestAction, AgentRecommendation, JeopardySite, DormantSignal } from '../lib/types'

const DAY = 86400000

const SEV: Record<CardSeverity, { color: string; Icon: typeof AlertTriangle; label: string }> = {
  red: { color: '#d4380d', Icon: AlertTriangle, label: 'Critical' },
  amber: { color: '#d48806', Icon: AlertCircle, label: 'Watch' },
  info: { color: '#0958d9', Icon: Info, label: 'Info' },
  good: { color: '#389e0d', Icon: CheckCircle2, label: 'On track' },
}

// Calendar days from today to a date (for the "N days left" DISPLAY — a countdown to a calendar
// event reads naturally in calendar days).
function calendarDaysTo(iso: string | null): number | null {
  if (!iso) return null
  return Math.round((new Date(iso).getTime() - Date.now()) / DAY)
}

// Working days from today to a date, weekends excluded (for the imminence HIGHLIGHT trigger — a
// slippage-against-deadline concern, so working days, mirroring the server's imminence threshold).
function workingDaysTo(iso: string | null): number | null {
  if (!iso) return null
  const start = new Date()
  const end = new Date(iso)
  const s = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate())
  const e = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate())
  if (e === s) return 0
  const sign = e > s ? 1 : -1
  let count = 0
  for (let c = Math.min(s, e) + DAY; c <= Math.max(s, e); c += DAY) {
    const dow = new Date(c).getUTCDay()
    if (dow !== 0 && dow !== 6) count++
  }
  return sign * count
}

export function IntelligencePage() {
  const navigate = useNavigate()
  // NBA result + cards-expanded persist in the page store so a generated recommendation
  // and the expand state survive navigating away and back. Loading/error/riskExpanded stay
  // local (transient). cardsExpanded defaults true on first visit via the store's initial slice.
  const cardsExpanded = useIntelligenceSlice((s) => s.cardsExpanded)
  const setCardsExpanded = storeSetCardsExpanded
  const nba = useIntelligenceSlice((s) => s.nba) as AgentNextBestAction | null
  const setNba = storeSetNba
  const [riskExpanded, setRiskExpanded] = useState(true)
  const [nbaExpanded, setNbaExpanded] = useState(true)
  const [nbaLoading, setNbaLoading] = useState(false)
  const [nbaError, setNbaError] = useState<string | null>(null)

  // Section anchors so a summary-strip card can scroll to (and expand) the section it summarises.
  const signalsRef = useRef<HTMLDivElement>(null)
  const riskRef = useRef<HTMLDivElement>(null)
  const nbaRef = useRef<HTMLDivElement>(null)
  const revealSignals = () => { setCardsExpanded(true); signalsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }
  const revealRisk = () => { setRiskExpanded(true); riskRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }
  const revealNba = () => { setNbaExpanded(true); nbaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }

  const { data: tower, loading: towerLoading } = useAsync<ControlTowerResponse>(() => api.controlTower(), [])
  const { data: insights, loading: insightsLoading, error, reload } = useAsync<Insights>(() => api.insights(), [])

  const loading = towerLoading || insightsLoading
  if (loading && !insights) return <div className="space-y-4"><SectionTitle>Intelligence</SectionTitle><Skeleton className="h-60" /></div>
  if (error || !insights) return <Card className="p-4"><ErrorState message={error || 'Failed'} onRetry={reload} /></Card>

  const sites = insights.jeopardy
  // TRUE totals come from the server (computed over ALL at-risk sites, not the rendered list) so the
  // strip/header are honest. imminent = within the threshold OR already overdue (an overdue migration
  // is the most urgent, not "no longer imminent").
  const atRiskTotal = insights.jeopardy_count
  const imminentCount = insights.imminent_count
  // Imminence threshold, sourced from the control tower (same value the server uses).
  const imminentWd = tower?.imminent_wd ?? 10

  async function getRecommendations() {
    revealNba()
    setNbaLoading(true); setNbaError(null)
    try { setNba(await api.agentNba({})) }
    catch (e) { setNbaError(e instanceof ApiError ? e.message : 'Could not generate recommendations') }
    finally { setNbaLoading(false) }
  }

  return (
    <div className="space-y-4">
      <SectionTitle sub="Programme risks, proactive insights, and recommended actions — all in one place">
        <span className="inline-flex items-center gap-2"><Shield className="h-5 w-5 text-brand-600" /> Intelligence</span>
      </SectionTitle>

      {/* Summary strip — three labelled clusters, ordered to match the sections below (Programme
          Signals, then Site Risk, then Recommended Actions). Each cluster jumps to + expands its own
          section. Held on ONE row (no wrap) — the shared shell flexes each to an equal share. Two
          distinct lenses: programme-vs-plan signals vs per-site T-minus risk (state-vs-diagnosis). */}
      <div className="flex items-stretch gap-3">
        {/* Cluster 1 — programme signals (control-tower cards by severity; a card count, not a site list).
            Whole bar jumps to the Programme Signals section below. */}
        {tower && (
          <StripCluster label="Programme Signals" icon={Radar} iconColor="var(--brand-600, #2563eb)" onJump={revealSignals}>
            <div className="flex items-center gap-1.5" title="Programme health vs the delivery plan (migrated vs planned-to-date)">
              <span className="text-[10px] text-muted">Health</span>
              <RagDot rag={insights.headline.rag} />
              <span className="text-[9px] text-slate-400">vs plan</span>
            </div>
            <Stat label="Critical" value={tower.counts.red} tone={tower.counts.red > 0 ? 'red' : 'green'} />
            <Stat label="Watch" value={tower.counts.amber} tone={tower.counts.amber > 0 ? 'amber' : 'green'} />
          </StripCluster>
        )}
        {/* Cluster 2 — per-site risk (measured against each site's own T-minus deadlines). Whole bar
            jumps to the Sites at Risk table below (not out to the Sites portal). */}
        <StripCluster label="Sites at Risk" icon={AlertTriangle} iconColor="var(--rag-red, #dc2626)" onJump={revealRisk}>
          <Stat label="At Risk" value={atRiskTotal} tone={atRiskTotal > 0 ? 'red' : 'green'} />
          <span className="text-slate-300">›</span>
          <Stat label="of which imminent" value={imminentCount} tone={imminentCount > 0 ? 'amber' : 'green'} />
        </StripCluster>
        {/* Cluster 3 — AI recommended actions. Same shell as the other strips (whole bar navigates to
            the tile), plus a nested button that GENERATES then navigates. The strip body is a div (not
            a button) so the inner button is legal — the original baseline strip pattern. */}
        <NbaStrip
          hasResult={!!nba}
          loading={nbaLoading}
          onView={revealNba}
          onGenerate={getRecommendations}
        />
      </div>

      {/* NBA recommendations — anchored at the TOP (under the strip) so the "Get Recommendations"
          strip button has a stable, in-view target to scroll to. Collapsible header mirrors the
          Programme Signals / Sites at Risk sections exactly. */}
      {(nba || nbaError || nbaLoading) && (
        <CollapsibleCard containerRef={nbaRef} icon={Sparkles} iconClass="text-brand-600"
          title={`Recommended Actions${nba ? ` (${nba.recommendations.length})` : ''}`}
          expanded={nbaExpanded} onToggle={() => setNbaExpanded(!nbaExpanded)}>
          <div className="border-t border-line p-4">
            {nbaLoading && !nba && <Skeleton className="h-20" />}
            {nbaError && <p className="text-[12px] text-rag-red">{nbaError}</p>}
            {nba && (
              <div className="space-y-2.5">
                {nba.summary && <p className="text-[12px] leading-relaxed text-muted">{nba.summary}</p>}
                {nba.recommendations.map((r, i) => <RecommendationRow key={i} rec={r} rank={i + 1} navigate={navigate} />)}
                <p className="pt-0.5 text-[10px] text-slate-400">AI-generated · advisory only</p>
              </div>
            )}
          </div>
        </CollapsibleCard>
      )}

      {/* Programme-level cards (Control Tower) — collapsible */}
      {tower && (
        <CollapsibleCard containerRef={signalsRef} icon={Radar} iconClass="text-brand-600"
          title={`Programme Signals (${tower.cards.length} triggered)`}
          expanded={cardsExpanded} onToggle={() => setCardsExpanded(!cardsExpanded)}>
          <div className="border-t border-line">
            {tower.cards.length > 0 ? (
              <div className="grid gap-3 p-4 lg:grid-cols-2">
                {tower.cards.map((card) => <TowerCard key={card.id} card={card} />)}
              </div>
            ) : (
              <p className="px-4 py-6 text-center text-[12px] text-muted">No active signals — the programme is healthy on every monitored dimension.</p>
            )}
            {tower.dormant.length > 0 && <DormantSignals dormant={tower.dormant} />}
          </div>
        </CollapsibleCard>
      )}

      {/* Site-level risk table (Jeopardy) — flat, worst-first, columnar, collapsible */}
      <CollapsibleCard containerRef={riskRef} icon={AlertTriangle} iconClass="text-rag-red"
        title={`Sites at Risk (${atRiskTotal})`}
        expanded={riskExpanded} onToggle={() => setRiskExpanded(!riskExpanded)}
        headerRight={<span className="text-[11px] text-muted">Soonest migration first · <Clock className="inline h-3 w-3 text-rag-amber" /> = imminent</span>}>
        {(
          atRiskTotal === 0 ? (
            <div className="border-t border-line"><EmptyState icon="✓" title="No sites at risk" hint="All sites are tracking to plan." /></div>
          ) : (
            <div className="border-t border-line">
              <SiteRiskList sites={sites} navigate={navigate} imminentWd={imminentWd} />
              {atRiskTotal > sites.length && (
                <button onClick={() => navigate('/?slicer=jeopardy')}
                  className="flex w-full items-center justify-center gap-1 border-t border-line bg-canvas/50 px-4 py-2 text-[11px] font-medium text-brand-600 hover:bg-canvas transition-colors">
                  Showing top {sites.length} of {atRiskTotal} — view all in the Site Portal <ArrowRight className="h-3 w-3" />
                </button>
              )}
            </div>
          )
        )}
      </CollapsibleCard>
    </div>
  )
}

// ---- Sub-components ----

// A collapsible section card — single source of the "titled header with a chevron toggle + bordered
// body" pattern used by all three Intelligence sections (Recommended Actions, Programme Signals,
// Sites at Risk). `headerRight` is an optional slot shown left of the chevron (Sites at Risk uses it
// for a hint). `containerRef` + `scroll-mt-4` give each section a stable scroll anchor.
function CollapsibleCard({ icon: Icon, iconClass, title, expanded, onToggle, headerRight, containerRef, children }: {
  icon: typeof AlertTriangle; iconClass: string; title: React.ReactNode; expanded: boolean; onToggle: () => void
  headerRight?: React.ReactNode; containerRef?: React.Ref<HTMLDivElement>; children: React.ReactNode
}) {
  return (
    <div ref={containerRef} className="scroll-mt-4"><Card className="overflow-hidden">
      <button onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-canvas transition-colors">
        <span className="flex items-center gap-2 text-[13px] font-semibold text-ink">
          <Icon className={`h-4 w-4 ${iconClass}`} /> {title}
        </span>
        <span className="flex items-center gap-3">
          {headerRight}
          {expanded ? <ChevronUp className="h-4 w-4 text-muted" /> : <ChevronDown className="h-4 w-4 text-muted" />}
        </span>
      </button>
      {expanded && children}
    </Card></div>
  )
}

// Shared strip shell — single source of the summary-strip look, so every cluster (whether its whole
// body navigates or it hosts an inner button) reads identically. The label header mirrors the
// matching section header's icon so the strip and its section read as the same thing.
// Strips sit on one row (container is flex-nowrap). Each sizes to its content and won't shrink
// (`flex-shrink-0`) so labels/values never truncate at desktop widths; the row scrolls rather than
// wraps in the rare event it can't fit.
const STRIP_SHELL = 'group flex flex-shrink-0 items-center gap-3 rounded-card border border-line bg-white px-4 py-3 text-left transition-colors hover:border-brand-200 hover:bg-brand-50/30'
function StripLabel({ label, icon: Icon, iconColor }: { label: string; icon: typeof AlertTriangle; iconColor: string }) {
  return (
    <span className="flex items-center gap-1 whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide text-muted transition-colors group-hover:text-brand-600">
      <Icon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: iconColor }} />
      {label}
    </span>
  )
}

// A labelled summary-strip cluster. The WHOLE bar is a button that jumps to (and expands) the
// matching section below — the metrics inside are display-only (no separate drill-throughs).
function StripCluster({ label, icon: Icon, iconColor, onJump, children }: { label: string; icon: typeof AlertTriangle; iconColor: string; onJump: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onJump} title={`Go to ${label}`} className={STRIP_SHELL}>
      <StripLabel label={label} icon={Icon} iconColor={iconColor} />
      {children}
    </button>
  )
}

// The AI-recommendations strip. Same shell as StripCluster, but the body is a div (role=button) so it
// can legally host a nested button: clicking the BODY only scrolls to the Recommended Actions tile
// ("view the existing one"); the nested button GENERATES then scrolls. This div+inner-button shape is
// the file's original baseline strip pattern (git 8ae1938).
function NbaStrip({ hasResult, loading, onView, onGenerate }: {
  hasResult: boolean; loading: boolean; onView: () => void; onGenerate: () => void
}) {
  return (
    <div role="button" tabIndex={0} onClick={onView} title="Go to Recommended Actions" aria-label="Go to Recommended Actions"
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onView() } }}
      className={`${STRIP_SHELL} cursor-pointer`}>
      <StripLabel label="Recommended Actions" icon={Sparkles} iconColor="var(--brand-600, #2563eb)" />
      <Button size="sm" variant="primary" disabled={loading} className="ml-auto flex-shrink-0"
        onClick={(e) => { e.stopPropagation(); onGenerate() }}>
        <Sparkles className="h-3.5 w-3.5" /> {loading ? 'Thinking…' : hasResult ? 'Refresh' : 'Generate'}
      </Button>
    </div>
  )
}

// Display-only metric inside a StripCluster (the whole cluster handles navigation).
function Stat({ label, value, tone, hint }: { label: string; value: number; tone: string; hint?: string }) {
  const color = tone === 'red' ? 'text-rag-red' : tone === 'amber' ? 'text-rag-amber' : tone === 'green' ? 'text-rag-green' : 'text-ink'
  return (
    <span title={hint} className="whitespace-nowrap">
      <span className="text-[10px] text-muted">{label}</span>
      <span className={`ml-1.5 text-[15px] font-bold tabular-nums ${color}`}>{value}</span>
    </span>
  )
}

// Map an NBA priority string onto the page's SEV colour vocabulary (same tokens the control-tower
// cards use), so a recommendation's priority reads in the same colours as programme severity.
function priorityColor(priority: string): string {
  const p = priority.toLowerCase()
  if (/critical/.test(p)) return SEV.red.color
  if (/high/.test(p)) return SEV.amber.color
  if (/medium|med/.test(p)) return SEV.info.color
  return '#64748b' // low / unknown → slate
}

// One recommendation, styled to mirror TowerCard (priority-tinted left accent + faint wash) so the
// NBA rows and the control-tower cards read as the same component family.
function RecommendationRow({ rec, rank, navigate }: { rec: AgentRecommendation; rank: number; navigate: (path: string) => void }) {
  const color = priorityColor(rec.priority || '')
  // "batch" is the literal site_id the model returns for a multi-site action — not a real site, so it
  // must not be a link. A single real site id keeps the drill-through; batch shows a static label.
  const isBatch = !rec.site_id || rec.site_id.toLowerCase() === 'batch'
  return (
    <div className="flex gap-3 rounded-md border-l-3 p-3" style={{ borderLeftColor: color, background: `${color}06` }}>
      <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-white text-[10px] font-bold tabular-nums shadow-sm" style={{ color }}>{rank}</span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {isBatch
            ? <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">Multiple sites</span>
            : <button onClick={() => navigate(`/?site_id=${rec.site_id}`)} className="text-[12px] font-semibold text-brand-700 underline decoration-dotted">{rec.site_id}</button>}
          <span className="text-[12px] font-semibold leading-snug text-ink">{rec.action}</span>
          {rec.priority && (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color, background: `${color}14` }}>{rec.priority}</span>
          )}
        </div>
        {rec.rationale && <p className="mt-1 text-[11px] leading-relaxed text-muted">{rec.rationale}</p>}
        {rec.impact && <p className="mt-1 flex items-start gap-1 text-[11px] leading-relaxed text-rag-green"><ArrowRight className="mt-0.5 h-3 w-3 flex-shrink-0" /> Unblocks: {rec.impact}</p>}
        <p className="mt-1.5 text-[10px] text-slate-400">{rec.owner}{rec.deadline ? ` · Deadline: ${rec.deadline}` : ''}</p>
      </div>
    </div>
  )
}

// A calendar "days to migration" pill: neutral normally, amber (cautionary) when imminent by the
// working-day threshold. Countdown shown in CALENDAR days (natural); imminence tested in WORKING days.
function DaysLeftPill({ migrationDate, imminentWd }: { migrationDate: string | null; imminentWd: number }) {
  const calDays = calendarDaysTo(migrationDate)
  const wdays = workingDaysTo(migrationDate)
  // Imminent = within the working-day threshold OR already past it (an overdue-but-unmigrated site is
  // the most urgent — it doesn't stop being imminent by going overdue). Matches server isMigrationImminent.
  const imminent = wdays != null && wdays <= imminentWd
  const overdue = calDays != null && calDays < 0
  const label = calDays == null ? '—'
    : overdue ? `${Math.abs(calDays)}d overdue`
    : calDays === 0 ? 'today'
    : `${calDays}d left`
  const cls = imminent
    ? 'bg-rag-amber/15 text-rag-amber border-rag-amber/30'
    : 'bg-slate-100 text-slate-500 border-transparent'
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold tabular-nums ${cls}`}
      title={imminent ? (overdue ? 'Migration date has passed — site not yet migrated' : `Migration imminent — within ${imminentWd} working days`) : 'Calendar days until the migration date'}>
      {imminent && <Clock className="h-3 w-3" />}{label}
    </span>
  )
}

// ---- Flat, columnar site-risk list (sorted worst-first by the server) ----
// Each row exposes the risk as scannable columns — Site · Stuck at (stage) · Why (short cause) ·
// Owner · Migration date · days-left pill · View — rather than one red run-on sentence.
function SiteRiskList({ sites, navigate, imminentWd }: { sites: JeopardySite[]; navigate: (path: string) => void; imminentWd: number }) {
  return (
    <div className="max-h-[520px] overflow-auto">
      <table className="w-full text-[12px]">
        <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--line,#e5e7eb)]">
          <tr className="text-left text-[10px] uppercase tracking-wide text-muted">
            <th className="bg-white px-4 py-2 font-semibold">Site</th>
            <th className="bg-white px-3 py-2 font-semibold">Stuck at</th>
            <th className="bg-white px-3 py-2 font-semibold">Why</th>
            <th className="bg-white px-3 py-2 font-semibold">Owner</th>
            <th className="bg-white px-3 py-2 font-semibold text-right">Migration</th>
            <th className="bg-white px-3 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line/40">
          {sites.map((s) => (
            <tr key={s.site_id} className="hover:bg-canvas transition-colors">
              <td className="px-4 py-2 align-middle">
                <div className="flex flex-col">
                  <span className="font-semibold text-brand-700">{s.site_id}</span>
                  {s.site_name && <span className="text-[11px] text-muted truncate max-w-[140px]">{s.site_name}</span>}
                </div>
              </td>
              <td className="px-3 py-2 align-middle">
                <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-ink whitespace-nowrap">{s.stuck_stage || s.stage_label}</span>
              </td>
              <td className="px-3 py-2 align-middle text-ink">{s.cause ? s.cause.charAt(0).toUpperCase() + s.cause.slice(1) : '—'}</td>
              <td className="px-3 py-2 align-middle text-muted whitespace-nowrap">{s.owner || '—'}</td>
              <td className="px-3 py-2 align-middle text-right">
                <div className="flex flex-col items-end gap-0.5">
                  <span className="tabular-nums text-muted">{fmtDate(s.migration_date)}</span>
                  <DaysLeftPill migrationDate={s.migration_date} imminentWd={imminentWd} />
                </div>
              </td>
              <td className="px-3 py-2 align-middle text-right">
                <button onClick={() => navigate(`/?site_id=${s.site_id}`)}
                  className="inline-flex items-center gap-1 rounded border border-line px-2 py-1 text-[11px] font-medium text-brand-600 hover:bg-brand-50 hover:border-brand-200 transition-colors whitespace-nowrap">
                  View <ArrowRight className="h-3 w-3" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// The catalogue of monitored rules that did NOT trigger this run — shown title-only (greyed, no
// figure) under a collapsed row, so the by-exception tower is legible: the user can see the full set
// of signals being watched, and understands a rule's absence means "healthy", not "missing".
function DormantSignals({ dormant }: { dormant: DormantSignal[] }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-t border-line/60 bg-canvas/40">
      <button onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-4 py-2 text-left text-[11px] text-muted hover:text-ink transition-colors">
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        <span className="font-medium">{dormant.length} signal{dormant.length === 1 ? '' : 's'} monitored · not currently triggered</span>
      </button>
      {open && (
        <ul className="px-4 pb-3 pt-0.5">
          {dormant.map((d) => (
            <li key={d.id} className="flex items-baseline gap-2 py-1 text-[11px]">
              <CheckCircle2 className="h-3 w-3 flex-shrink-0 text-rag-green/60" />
              <span className="font-medium text-slate-500">{d.title}</span>
              <span className="text-slate-400">— watches for {d.description}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function TowerCard({ card }: { card: ControlTowerCard }) {
  const navigate = useNavigate()
  const sev = SEV[card.severity]
  const [feedback, setFeedback] = useState<boolean | null>(null)

  async function sendFeedback(useful: boolean) {
    setFeedback(useful)
    await api.feedback({ feedback_type: 'control_tower', reference_id: card.id, useful, metric: card.metric, severity: card.severity, title: card.title }).catch(() => {})
  }

  return (
    <div className="flex gap-3 rounded-md border-l-3 p-3" style={{ borderLeftColor: sev.color, background: `${sev.color}06` }}>
      <sev.Icon className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: sev.color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h4 className="text-[12px] font-semibold text-ink leading-snug">{card.title}</h4>
            {card.subtitle && <p className="text-[11px] font-medium leading-snug" style={{ color: sev.color }}>{card.subtitle}</p>}
          </div>
          <span className="flex-shrink-0 text-right">
            <span className="block text-[18px] font-bold tabular-nums leading-none" style={{ color: sev.color }}>{card.figure}</span>
            <span className="block text-[8px] uppercase tracking-wide text-slate-400" title="Computed from live site data — not an estimate">calculated</span>
          </span>
        </div>
        {card.body && <p className="mt-1.5 text-[11px] text-muted leading-relaxed">{card.body}</p>}
        <div className="mt-2 flex items-center gap-2">
          {card.drill && <button onClick={() => navigate(card.drill!)} className="text-[11px] font-medium text-brand-600 hover:underline inline-flex items-center gap-1">View sites <ArrowRight className="h-3 w-3" /></button>}
          {card.owner && <span className="text-[10px] text-slate-400">Owner: {card.owner}</span>}
          <div className="ml-auto flex items-center gap-1">
            {feedback == null ? (
              <>
                <button onClick={() => sendFeedback(true)} className="rounded p-0.5 text-slate-300 hover:text-rag-green"><ThumbsUp className="h-3 w-3" /></button>
                <button onClick={() => sendFeedback(false)} className="rounded p-0.5 text-slate-300 hover:text-rag-red"><ThumbsDown className="h-3 w-3" /></button>
              </>
            ) : <span className="text-[9px] text-muted">✓</span>}
          </div>
        </div>
      </div>
    </div>
  )
}
